// medplum-client.js — the ONE real Medplum session per browser for every
// ONLINE-PRIMARY staff screen (queue, reception, patients, schedule, reports…).
//
// SINGLE-SESSION MODEL (the cutover): the browser holds exactly one logged-in
// Medplum session, started by Login.jsx via loginWithPassword() and restored
// from localStorage (prefix 'medplum.user.') on later page loads. That one
// session drives BOTH the login gate and every FHIR read/write — there is no
// per-identity auto-login any more. The demo email/password entries in
// /assets/medplum-demo.json remain for NODE HARNESSES only; the app never
// reads them to log in, and the cfg object handed to screens is stripped of
// them (see appBundle) so a straggler cfg.currentUser reader fails loud
// instead of silently impersonating the demo doctor.
//
// MedplumClient persists its session to window.localStorage and refreshes the
// access token from the refresh token before it expires (refreshIfExpired,
// called internally on every request). When the refresh token itself is dead,
// the client clears its active login, getProfileAsync() resolves undefined,
// and getMedplum() throws NotLoggedInError — App.jsx turns that into a /login
// redirect (no retry loops).
//
// currentUser is DERIVED from live Medplum resources (deriveCurrentUser), not
// from static demo JSON: role from the server-provided ProjectMembership
// (auth/me) + PractitionerRole, department from the dept Organization's
// department-slug identifier. Cached per profile; recomputed on login.
import { MedplumClient, ClientStorage } from '@medplum/core';
import { setClinicTimezone } from '@healther/clinical-core/clinic-time.js';
import { computeCurrentUser } from '@healther/medplum-data/current-user.js';
import { setServerBase } from './net-base.js';
import { navTo } from './opd-nav.js';
import { isNetworkError } from './net-error.js';

const STORAGE_PREFIX = 'medplum.user.';

// ── Transport resilience for THE staff session (owner-decided 2026-08-09) ───
// Hospital wifi stalls and tablets on weak access points are the real failure
// mode this exists for: ONE generous timeout + ONE retry, transport-level only.
//
//   • 30s timeout per attempt ("it should be generous" — a stalled request on
//     clinic wifi is cut and retried once, not left spinning forever).
//   • Exactly ONE retry, ONLY on a network-level failure (fetch TypeError /
//     "Failed to fetch" / our own timeout — isNetworkError is the classifier).
//     No HTTP response arrived, so re-sending cannot double-execute a write.
//   • An HTTP RESPONSE — any status, including 4xx/5xx — is NEVER retried:
//     a response means the server received (and may already have executed)
//     the request; re-sending a POST would double-execute it.
//   • A caller-initiated abort (options.signal) is honoured and never retried.
//   • The second failure propagates loud (§1.3 — no silent retry loops).
//
// WHY a wrapped `fetch` instead of @medplum/core's own retry: MedplumClient
// 5.1.28 DOES ship a retry loop (client.ts fetchWithRetry), but it re-sends on
// HTTP 429/5xx RESPONSES — exactly the double-execute hazard ruled out above —
// and it throws immediately on AbortError, so an abort-based timeout would
// never be retried by it. We therefore pass `maxRetries: 0` to switch the
// library loop OFF and make this wrapper the single retry/timeout authority.
// The constructor's `fetch` option is the supported seam for this
// (client.ts: `this.fetch = options?.fetch ?? getDefaultFetch()`).
export const MEDPLUM_FETCH_TIMEOUT_MS = 30_000;

/**
 * Builds the fetch implementation handed to MedplumClient. Exported as a
 * factory (injectable baseFetch/timeoutMs) purely so the retry policy is
 * unit-testable; production callers use the no-arg default.
 */
export function createResilientFetch({ baseFetch, timeoutMs = MEDPLUM_FETCH_TIMEOUT_MS } = {}) {
  const doFetch = baseFetch || ((url, options) => fetch(url, options));

  // One attempt = the underlying fetch under a timeout AbortController that
  // also relays the caller's own signal (so user cancellation still works).
  const attempt = async (url, options) => {
    const callerSignal = options.signal;
    if (callerSignal && callerSignal.aborted) {
      throw callerSignal.reason || new DOMException('Aborted', 'AbortError');
    }
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
    const onCallerAbort = () => controller.abort(callerSignal.reason);
    if (callerSignal) callerSignal.addEventListener('abort', onCallerAbort);
    try {
      return await doFetch(url, { ...options, signal: controller.signal });
    } catch (err) {
      if (timedOut) {
        // Rebrand OUR abort as a timeout so the retry policy (and any log)
        // can tell it apart from a caller cancellation.
        const e = new Error('Medplum request timed out after ' + timeoutMs + 'ms');
        e.name = 'TimeoutError';
        e.cause = err;
        throw e;
      }
      throw err;
    } finally {
      clearTimeout(timer);
      if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
    }
  };

  return async function resilientFetch(url, options = {}) {
    try {
      return await attempt(url, options); // any HTTP response returns here — never retried
    } catch (err) {
      const callerAborted = !!(options.signal && options.signal.aborted);
      const transient = !callerAborted && (err.name === 'TimeoutError' || isNetworkError(err));
      if (!transient) throw err; // real fault → fail loud, no retry
      return attempt(url, options); // the ONE retry; a second failure propagates loud
    }
  };
}

// Typed auth failure: "there is no live Medplum session". App.jsx catches this
// (instanceof) on staff routes and redirects to /login preserving state.from.
export class NotLoggedInError extends Error {
  constructor(message = 'Not logged in to Medplum') {
    super(message);
    this.name = 'NotLoggedInError';
  }
}

/**
 * Pure classifier for a failed/empty session resume: is the stored session merely
 * UNREACHABLE right now (offline — keep working from the cache) or genuinely GONE
 * (route to /login)? Every environment fact is passed in so this stays pure and
 * unit-testable — it is the ONE place the offline-vs-expired policy lives.
 *
 * Why this matters: a NETWORK failure while a login is still on disk means "can't
 * reach /auth/me right now", NOT "logged out". A shared clinic tablet that drops
 * wifi must keep working from cache + outbox, not bounce every user to a login
 * screen it also can't reach. Only a definitively dead session (no stored login,
 * or a server-REJECTED refresh) is 'unauth'.
 *
 * @param {{ networkFailure: boolean, hasStoredLogin: boolean }} ctx
 * @returns {'offline' | 'unauth'} 'offline' → keep the session, render from cache;
 *   'unauth' → the session is gone, go to /login.
 */
export function classifySessionResume({ networkFailure = false, hasStoredLogin = false } = {}) {
  return networkFailure && hasStoredLogin ? 'offline' : 'unauth';
}

// The @medplum/core `onUnauthenticated` callback (wired into THE staff client in
// getClient below). The library invokes it ONLY on a server-REJECTED token refresh
// — true auth death: the refresh token itself is dead/revoked — or a 401 with no
// refresh possible. A NETWORK failure during a refresh never reaches here (the
// client rethrows WITHOUT clearing the login and WITHOUT calling this), so this
// fires for genuine expiry only, never for offline (verified in @medplum/core
// client.ts: fetchTokens catch → rethrow vs !response.ok → clearActiveLogin +
// onUnauthenticated). It routes to /login preserving where the user was (?next=),
// and is IDEMPOTENT: many concurrent 401s collapse to ONE redirect. The latch
// resets on the full-page load into /login (and if we are already on /login).
let _unauthRedirected = false;
export function _resetUnauthLatchForTest() { _unauthRedirected = false; } // test-only
export function onMedplumUnauthenticated() {
  if (_unauthRedirected) return;
  _unauthRedirected = true;
  currentUserCache = null; // drop derived role/department for the dead session
  let here = '/';
  try { here = window.location.pathname + window.location.search; } catch (_) {}
  if (here.startsWith('/login')) { _unauthRedirected = false; return; } // already there
  const next = '?next=' + encodeURIComponent(here);
  try {
    navTo('/login' + next, { replace: true });
  } catch (_) {
    try { window.location.replace('/login' + next); } catch (__) {}
  }
}

// Namespaces every localStorage key MedplumClient touches, so the staff
// session and the anonymous device sessions never clobber each other.
// MEDPLUM'S OWN ClientStorage, not a hand-rolled box (owner ruling
// 2026-08-11 after the makeKey incident: "we should just be using what
// Medplum has been giving us — it shouldn't be a high-maintenance thing").
// The SDK's class now takes a prefix natively — the ONE capability our old
// custom `PrefixedStorage` existed for — and its key shape is byte-identical
// (getItem(prefix + key)), so every already-stored session keeps working.
// Because the client and this storage now ship from the SAME package, the
// interface can never outgrow us again; the storage-contract test remains as
// the tripwire against anyone hand-rolling a replacement.
export function clientStorage(prefix) {
  return new ClientStorage(window.localStorage, prefix);
}

let configPromise = null;

/**
 * THE deployment-config read — the ONE fetch of /assets/medplum-demo.json in the
 * whole frontend, memoised for the page's lifetime.
 *
 * Exported because it is *also* what every Bot-id lookup needs (medplum-staff,
 * -duty, -oncall, -rooms, -designation, -approvals all read `cfg.bots.*`). Those
 * six each carried a byte-identical private copy of this function, i.e. six extra
 * fetches of the same file that could in principle disagree; they now call this.
 * The raw object is deliberately NOT the sanitized `appBundle()` cfg — Bot ids are
 * public deployment metadata, but callers of this see the unfiltered file, so do
 * not hand it to a screen (use getPublicAppConfig for that).
 *
 * WHY THIS ONE FETCH IS BARE-RELATIVE AND NOT net-base's assetUrl(): this file IS
 * the bootstrap — `serverBase` (the value assetUrl() prefixes with on Capacitor)
 * comes OUT of this very config, one line down in appBundle(). Resolving it
 * through assetUrl() would make the root of the dependency chain depend on its own
 * output: whether it hit the bundle or the server would depend on whether ota.js's
 * ensureServerBase() happened to win the race, and on a tablet booting offline the
 * server variant would simply fail and take the app down. The config JSON ships
 * INSIDE the app bundle precisely so it always loads same-origin (the same carve-out
 * net-base-guard.test.js documents). Phase 2 (React Native, where a relative URL has
 * no meaning at all) changes this ONE function, which is the point of consolidating
 * it here.
 */
export function loadRawConfig() {
  if (!configPromise) {
    configPromise = fetch('/assets/medplum-demo.json', { cache: 'no-store' }).then((res) => {
      if (!res.ok) throw new Error('medplum-demo config missing (' + res.status + ')');
      return res.json();
    });
  }
  return configPromise;
}

// ── THE staff session ───────────────────────────────────────────────────────
let clientPromise = null;   // the ONE MedplumClient (constructed once per page)
let bundlePromise = null;   // sanitized { cfg, hospital } app config (identity-free)

// ── resourceModified fan-out (write → read-cache invalidation, ONE place) ────
// @medplum/core ≥5.1.28 emits a 'resourceModified' client event after every
// successful createResource / createResourceIfNoneExist / updateResource /
// upsertResource / patchResource / deleteResource (and manual
// notifyResourceModified). We forward it to registered handlers — today the
// SWR read-cache invalidator in medplum-live.js — so ONE listener invalidates
// cached reads after every write, instead of each write site remembering to.
// Handlers may register before or after the client exists (the fan-out reads
// the live Set at dispatch time). Each handler is individually wrapped: a
// cache-invalidation failure must NEVER propagate into (or fail) the clinical
// write that fired it. Not silently swallowed either — console.warn ONCE per
// session, the same fail-loud-once pattern medplum-live.js uses for its
// subscription-socket errors.
const resourceModifiedHandlers = new Set();
let _resourceModifiedWarned = false;

/**
 * Register a handler for the staff client's 'resourceModified' events. The
 * handler receives the event payload: { resourceType, operation, id?, resource? }.
 * Returns an unregister function.
 */
export function onResourceModified(handler) {
  if (typeof handler !== 'function') return () => {};
  resourceModifiedHandlers.add(handler);
  return () => { resourceModifiedHandlers.delete(handler); };
}

/**
 * Attach the fan-out listener to a client. Called once by getClient() on THE
 * staff client (kiosk device clients never drive the staff read cache).
 * Exported so the wrap contract — a throwing handler cannot fail the write
 * that triggered it — is testable against the exact production code path.
 */
export function attachResourceModifiedFanout(medplum) {
  // Method guard for TEST DOUBLES only (same shape as invalidateCached's):
  // several harnesses stub MedplumClient as a bare class without its
  // EventTarget surface. The real client always has addEventListener.
  if (!medplum || typeof medplum.addEventListener !== 'function') return medplum;
  medplum.addEventListener('resourceModified', (event) => {
    for (const handler of resourceModifiedHandlers) {
      try {
        handler(event.payload);
      } catch (e) {
        if (!_resourceModifiedWarned) {
          _resourceModifiedWarned = true;
          console.warn('[medplum] a resourceModified handler threw — read-cache invalidation degraded (the write itself is unaffected):', e && e.message);
        }
      }
    }
  });
  return medplum;
}

function getClient() {
  if (!clientPromise) {
    clientPromise = loadRawConfig().then((raw) =>
      attachResourceModifiedFanout(new MedplumClient({
        baseUrl: raw.baseUrl,
        storage: clientStorage(STORAGE_PREFIX),
        // Transport resilience (createResilientFetch above): 30s timeout + ONE
        // network-failure-only retry live in OUR fetch. maxRetries: 0 turns the
        // library's own retry loop OFF — it would re-send on HTTP 429/5xx
        // responses, the double-execute hazard the owner ruled out.
        fetch: createResilientFetch(),
        maxRetries: 0,
        // OPTIONAL dedicated tablet ClientApplication. When the deploy config
        // carries a `clientId`, this session is governed by that ClientApplication
        // — its `refreshTokenLifetime` becomes the clinic-tablet session length
        // (the per-client lever; see scripts/medplum/provision-tablet-client.mjs).
        // Absent → '' (library default) → the Medplum server's default refresh
        // lifetime (2 weeks). MedplumClient threads this clientId into both the
        // login request and every refresh (client.ts startLogin / runRefreshWithLock).
        clientId: raw.clientId || undefined,
        // True auth death only: a server-REJECTED refresh routes to /login. Offline
        // never reaches this (see onMedplumUnauthenticated).
        onUnauthenticated: onMedplumUnauthenticated,
      })));
  }
  return clientPromise;
}

// The app-visible config is public deployment metadata only. In particular,
// anon ClientApplication credentials are never bundle configuration: a browser
// secret is public, and Medplum also archives every direct Bot input that such
// a client submits. Anonymous flows terminate credentials at the existing
// same-origin gateways and receive only non-secret hospital/Bot metadata here.
function appBundle() {
  if (!bundlePromise) {
    bundlePromise = loadRawConfig().then((raw) => {
      const cfg = {};
      for (const [k, v] of Object.entries(raw)) {
        if (k === 'email' || k === 'password' || k === 'currentUser' || k === 'anonClients') continue;
        if (v && typeof v === 'object' && !Array.isArray(v) && ('password' in v || 'currentUser' in v)) continue;
        cfg[k] = v;
      }
      // ONE config point for clinic-local time across every screen: the clinic
      // IANA timezone (default Asia/Kolkata) drives all wall-clock <-> instant
      // conversion in lib/clinic-time.js. Set it here — the single choke point
      // every screen's getMedplum()/appBundle() passes through.
      setClinicTimezone(raw.hospital && raw.hospital.timezone);
      // Plumb the app's OWN-server base (Express /api, /uploads, /assets, WS) into
      // net-base. Absent on web (same-origin) → net-base stays the identity fn;
      // present on the Capacitor build → those legs become absolute. This is the
      // one config choke point every screen already passes through.
      setServerBase(raw.serverBase);
      return { cfg, hospital: raw.hospital };
    });
  }
  return bundlePromise;
}

/** Public, identity-free application configuration for anonymous boot screens.
 * This deliberately does not construct or resume any Medplum session. */
export async function getPublicAppConfig() {
  const { cfg } = await appBundle();
  return cfg;
}

/**
 * Staff login — the ONE real Medplum password login (Login.jsx). PKCE is
 * auto-injected by @medplum/core (startLogin → ensureCodeChallenge;
 * processCode sends the verifier). On success the session is persisted under
 * 'medplum.user.*' and the derived-currentUser cache is reset for the new
 * principal. Returns the same session bundle getMedplum() resolves to.
 */
export async function loginWithPassword({ email, password }) {
  const raw = await loadRawConfig();
  const projectId = typeof raw.projectId === 'string' ? raw.projectId.trim() : '';
  if (!projectId) {
    throw new Error('Medplum login configuration is missing projectId (/assets/medplum-demo.json)');
  }
  const medplum = await getClient();
  // `offline_access` is what makes the server issue a REFRESH token. Without it
  // (the old call) the server defaulted the scope to bare `openid`, issued NO
  // refresh token, and every session hard-died at the 1-hour access-token
  // lifetime — the shared-tablet expiry bug. `offline_access` is the OAuth-
  // standard scope Medplum honours (server oauth/utils.ts includeRefreshToken:
  // `scopeArray.includes('offline_access')`; the deprecated `offline` also works
  // but warns). A dedicated tablet ClientApplication with `refresh_token` in its
  // grantType would also trigger it, but requesting the scope keeps refresh
  // tokens flowing even on the default (no-clientId) config.
  let login;
  try {
    login = await medplum.startLogin({
      email,
      password,
      projectId,
      scope: 'openid offline_access',
    });
    if (!login.code) {
      // Multiple project memberships would need a pick-project step we do not
      // have (one hospital = one project). Fail loud rather than guess.
      throw new Error(login.memberships
        ? 'This account belongs to multiple Medplum projects — project selection is not supported'
        : 'Medplum login did not return an authorization code');
    }
    await medplum.processCode(login.code);
  } catch (err) {
    // FAIL CLEAN (found by verify-login-cutover 1.2 on the 5.1.28 client):
    // startLogin writes its PKCE handshake state (pkceState / codeVerifier)
    // into storage BEFORE the login POST, and a failed login leaves those keys
    // behind. They are pre-auth artifacts — no token, no session — but the
    // invariant is "a failed login persists NOTHING under medplum.user.*", so
    // sweep them before rethrowing the original error.
    try {
      window.localStorage.removeItem('medplum.user.pkceState');
      window.localStorage.removeItem('medplum.user.codeVerifier');
    } catch (_) { /* storage unavailable — nothing persisted anyway */ }
    throw err;
  }
  currentUserCache = null; // new principal → rederive role/department
  return getMedplum();
}

/**
 * Returns { medplum, cfg, hospital } for THE logged-in staff session, restored
 * from storage if needed — or throws NotLoggedInError when there is no live
 * session (never logged in, or the refresh token expired and the client
 * cleared itself). The profile check runs on EVERY call (cheap after init) so
 * an expired session surfaces NotLoggedInError at the next navigation.
 *
 * The identity argument is accepted and IGNORED: pre-cutover callers pass
 * 'admin'/'receptionist' etc., but there is only one session now — the
 * logged-in user either has the breadth or the App.jsx role gate bounces them.
 */
export async function getMedplum(_identity) {
  const medplum = await getClient();
  // getProfile() races the constructor's fire-and-forget session resume (it
  // sets the access token synchronously but fetches /auth/me async) — always
  // undefined immediately after construction even when a valid session is
  // restored from localStorage. getProfileAsync() awaits that resume first.
  //
  // OFFLINE ≠ EXPIRED: on a cold OFFLINE boot the resume's /auth/me fetch
  // REJECTS with a network error (or, with no stored login, resolves undefined).
  // A stored login + a network failure means the session is intact and simply
  // unreachable — we must keep working from the SWR cache, NOT throw
  // NotLoggedInError (which App.jsx would turn into a /login redirect the tablet
  // can't even reach). classifySessionResume() is the single discriminator.
  let profile;
  try {
    profile = await medplum.getProfileAsync();
  } catch (err) {
    const hasStoredLogin = !!activeLoginProfileId();
    if (classifySessionResume({ networkFailure: isNetworkError(err), hasStoredLogin }) === 'offline') {
      const { cfg, hospital } = await appBundle();
      return { medplum, cfg, hospital }; // offline: keep the session, reads hit cache
    }
    // Not a keep-working-offline case. If the stored login is gone, the client
    // cleared it on a server-rejected refresh (true auth death) → NotLoggedInError
    // routes to /login (onUnauthenticated has usually already fired the redirect;
    // this keeps the loader path consistent). Session still on disk + a
    // non-network error is a real fault → fail loud.
    if (!hasStoredLogin) throw new NotLoggedInError();
    throw err;
  }
  if (!profile) {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const hasStoredLogin = !!activeLoginProfileId();
    if (classifySessionResume({ networkFailure: offline, hasStoredLogin }) === 'offline') {
      const { cfg, hospital } = await appBundle();
      return { medplum, cfg, hospital };
    }
    throw new NotLoggedInError();
  }
  const { cfg, hospital } = await appBundle();
  return { medplum, cfg, hospital };
}

/**
 * The logged-in user's Medplum profile id, resolved SYNCHRONOUSLY from the
 * session restored in localStorage — with NO network call. On a cold OFFLINE
 * launch @medplum/core has the active login (access token + profile reference)
 * from storage, but `getProfile()` stays undefined until the /auth/me resume
 * succeeds (which it can't offline). The stored LoginState still carries
 * `profile.reference` ('Practitioner/<id>'), so this returns `<id>` offline. Used
 * by swrLoad to key the per-user SWR cache the SAME way online and offline —
 * otherwise a cold offline read keys under 'anon', misses the persisted rows, and
 * the offline loader throws "Failed to fetch". Returns null when no session is
 * stored (genuinely logged out). Reads the SAME 'medplum.user.activeLogin' key
 * MedplumClient persists (see authHeader()).
 */
export function activeLoginProfileId() {
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_PREFIX + 'activeLogin');
  } catch (_) {
    return null; // storage unavailable (privacy mode)
  }
  if (!raw) return null;
  try {
    const ref = JSON.parse(raw).profile && JSON.parse(raw).profile.reference;
    return ref ? ref.split('/').pop() : null; // 'Practitioner/<id>' → '<id>'
  } catch (_) {
    return null; // corrupt entry — treat as no session
  }
}

/**
 * READ-AFTER-WRITE: drop MedplumClient's cached GETs for the resource types a
 * write just changed. MedplumClient memoises search/read GETs for ~60s, so a
 * re-read taken immediately after a write — ours OR a Bot's, since executeBot is
 * a POST the SDK cannot associate with any cached GET key — can serve the
 * PRE-write resource. That is the duty-toggle bug (owner hit it live on demo
 * 2026-08-05: the write landed, the pill never flipped, repeated taps silently
 * toggled his real state), and the same shape sits behind every write→re-read
 * pair in this app.
 *
 * Call this at the WRITE SOURCE (inside the lib function that wrote), typed to
 * exactly what changed, so EVERY reader is covered — not just the writing
 * screen's own optimistic state. This is the medplum-booking.js / medplum-duty.js
 * idiom, factored out because ~40 write functions need it.
 *
 * Best-effort by design: the durable write has already landed, the network
 * refetch remains authoritative, and a cache bust must never fail a clinical
 * write (nor must a test double lacking the method).
 */
export async function invalidateCached(...types) {
  try {
    const { medplum } = await getMedplum();
    if (!medplum || typeof medplum.invalidateSearches !== 'function') return;
    for (const t of types) if (t) medplum.invalidateSearches(t);
  } catch (_) { /* optional client cache — the refetch is still authoritative */ }
}

/**
 * Sign out: revoke the session server-side (medplum.signOut → POST
 * auth/logout + clear its own storage), then hard-clear every staff
 * 'medplum.*' localStorage key — including stale pre-cutover per-identity
 * prefixes (medplum.doctor./medplum.receptionist./…) that hold real tokens
 * from the auto-login era. Anonymous DEVICE sessions ('medplum.anon.*') are
 * deliberately kept: a staff logout must not disarm the waiting-room board or
 * kiosk credential on the same machine.
 */
export async function signOutMedplum() {
  currentUserCache = null;
  const medplum = await getClient();
  try {
    await medplum.signOut(); // POST auth/logout, then clears itself
  } catch (_) {
    // Server revocation failed (offline / rate-limited / already expired).
    // The LOCAL clear below is what actually logs this browser out — proceed.
  }
  // signOut() only clears in-memory auth state when the server POST succeeds
  // (verified live: a failed auth/logout left the session usable). clear() is
  // idempotent — always drop the in-memory login + the client's own storage.
  medplum.clear();
  for (const k of Object.keys(window.localStorage)) {
    if (k.startsWith('medplum.') && !k.startsWith('medplum.anon.')) window.localStorage.removeItem(k);
  }
}

/**
 * Bearer header for Express calls that survived the staff-session removal
 * (requireMedplumBearer routes). Reads THE staff session's access token
 * straight from its persisted storage ('medplum.user.activeLogin') — sync, no
 * client construction — so any fetch()/api() helper can spread it inline:
 * `headers: { ...authHeader() }`. Returns {} when there is no live session
 * (the server answers 401 and the app's NotLoggedInError path handles it).
 * MedplumClient writes every token refresh back to this same storage key, so
 * the value tracks the active session.
 */
export function authHeader() {
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_PREFIX + 'activeLogin');
  } catch (_) {
    return {}; // storage unavailable (privacy mode) — same as "not logged in"
  }
  if (!raw) return {};
  try {
    const token = JSON.parse(raw).accessToken;
    return token ? { Authorization: 'Bearer ' + token } : {};
  } catch (_) {
    return {}; // corrupt entry — unauthenticated request fails loud with 401
  }
}

// ── currentUser derivation ──────────────────────────────────────────────────
// The static cfg.currentUser is gone; role/department/name are derived from what
// Medplum ACTUALLY returns for the live session. The DERIVATION ITSELF is pure and
// lives in @healther/medplum-data/current-user.js (computeCurrentUser) so Phase 2's
// Expo app reuses it; read that file for the role/department resolution rules.
//
// What stays HERE is the part that is genuinely host-bound: the per-session memo
// below and the typed NotLoggedInError. The memo is cleared from three other places
// in this module (onMedplumUnauthenticated, the login path, signOutMedplum) — a
// signed-out or re-logged-in principal must never keep serving the previous user's
// derived role/department.
let currentUserCache = null; // { key: profile.id, promise } — per-session memo

/**
 * Derives { id, name, displayName, role, department } (the exact DTO shape
 * the old static cfg.currentUser had) from the live session. Cached per
 * profile; pass the session's medplum or let it resolve via getMedplum().
 * Throws NotLoggedInError when there is no live session.
 */
export function deriveCurrentUser(medplum) {
  const run = async () => {
    const client = medplum || (await getMedplum()).medplum;
    const profile = await client.getProfileAsync();
    if (!profile) throw new NotLoggedInError();
    if (!currentUserCache || currentUserCache.key !== profile.id) {
      const promise = computeCurrentUser(client, profile);
      currentUserCache = { key: profile.id, promise };
      // A failed derivation must not be memoized — the next call recomputes.
      promise.catch(() => {
        if (currentUserCache && currentUserCache.promise === promise) currentUserCache = null;
      });
    }
    return currentUserCache.promise;
  };
  return run();
}

// ── Kiosk DEVICE session ─────────────────────────────────────────────────────
// The reception kiosk is a PER-TABLET device credential provisioned by scripts/medplum/provision-kiosk-
// device.mjs and armed ONCE per physical tablet by staff. Its {clientId,
// clientSecret} are therefore stored locally (under medplum.anon.kioskDevice.*,
// inside the same anon firewall — signOutMedplum preserves medplum.anon.*), not
// in the shipped config. Bound to the opd-kiosk-device AccessPolicy: create-only
// intake writes + a hiddenFields-narrowed Patient demographics read, no chart.
const KIOSK_DEVICE_PREFIX = 'medplum.anon.kioskDevice.';
const KIOSK_CRED_KEY = KIOSK_DEVICE_PREFIX + 'cred';
const kioskDeviceSessions = new Map();

// One-time staff arming step. Validates the pair by actually logging in
// (startClientLogin); only persists on success so a mistyped secret never
// silently "arms" a dead kiosk. Returns the device profile for confirmation.
export async function armKioskDevice({ clientId, clientSecret } = {}) {
  if (!clientId || !clientSecret) throw new Error('armKioskDevice requires { clientId, clientSecret }');
  const cfg = await loadRawConfig();
  const medplum = new MedplumClient({ baseUrl: cfg.baseUrl, storage: clientStorage(KIOSK_DEVICE_PREFIX) });
  await medplum.startClientLogin(clientId, clientSecret); // throws on bad creds → not stored
  const profile = await medplum.getProfileAsync();
  // Persist the pair only after a proven login, so getMedplumKioskDevice can
  // re-authenticate on later boots without a fresh arming step.
  window.localStorage.setItem(KIOSK_CRED_KEY, JSON.stringify({ clientId, clientSecret }));
  kioskDeviceSessions.set('kioskDevice', Promise.resolve({ medplum, cfg, hospital: cfg.hospital }));
  return profile;
}

export function isKioskDeviceArmed() {
  return !!window.localStorage.getItem(KIOSK_CRED_KEY);
}

async function initKioskDeviceSession() {
  const raw = window.localStorage.getItem(KIOSK_CRED_KEY);
  if (!raw) throw new Error('kiosk device is not armed — run the staff arming step (armKioskDevice)');
  const { clientId, clientSecret } = JSON.parse(raw);
  const cfg = await loadRawConfig();
  const medplum = new MedplumClient({ baseUrl: cfg.baseUrl, storage: clientStorage(KIOSK_DEVICE_PREFIX) });
  if (!(await medplum.getProfileAsync())) {
    await medplum.startClientLogin(clientId, clientSecret);
  }
  return { medplum, cfg, hospital: cfg.hospital };
}

// Returns { medplum, cfg, hospital } for the armed kiosk device. Cached like
// staff singleton. Throws if the tablet has not been armed.
export function getMedplumKioskDevice() {
  if (!kioskDeviceSessions.has('kioskDevice')) {
    kioskDeviceSessions.set('kioskDevice', initKioskDeviceSession().catch((e) => { kioskDeviceSessions.delete('kioskDevice'); throw e; }));
  }
  return kioskDeviceSessions.get('kioskDevice');
}
